import React from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { useSeo } from "../lib/seo";
import { RESOURCES } from "../lib/resources";
import { BookOpen, ArrowRight, HelpCircle } from "lucide-react";

export default function Resources() {
  useSeo({
    title: "Auto Glass Resources & Guides — Lecis Auto Glass",
    description:
      "Guides and articles on side mirrors, windshields, backglass and sunroof glass for exotic, luxury and EV vehicles — fitment help, care tips and buying advice from Lecis Auto Glass.",
    canonicalPath: "/resources",
  });

  return (
    <StorefrontLayout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="resources-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-red-500 font-bold mb-6">
          Auto Glass Resources
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6">
          Guides, tips &amp; fitment help.
        </h1>
        <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-3xl mb-12">
          Practical guides on side mirrors, windshields, backglass and sunroof glass for exotic,
          luxury and EV vehicles — how to confirm fitment, care for premium glass, and choose between
          OEM-style and aftermarket options.
        </p>

        {RESOURCES.length === 0 ? (
          <div
            className="border border-white/10 rounded-md p-10 text-center"
            data-testid="resources-empty"
          >
            <BookOpen size={26} className="text-red-500 mx-auto mb-4" />
            <div className="font-display text-xl mb-2">New guides are on the way</div>
            <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
              We're preparing our auto glass resource library. In the meantime, our team is happy to
              help you confirm fitment before you buy.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              <Link
                to="/contact"
                data-testid="resources-contact-cta"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 h-11 text-sm rounded-sm transition"
              >
                Contact &amp; Fitment Help <ArrowRight size={15} />
              </Link>
              <Link
                to="/faq"
                data-testid="resources-faq-cta"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-zinc-200 px-5 h-11 text-sm rounded-sm transition"
              >
                <HelpCircle size={15} className="text-red-500" /> Read FAQs
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="resources-grid">
            {RESOURCES.map((r) => (
              <Link
                key={r.slug}
                to={`/resources/${r.slug}`}
                data-testid={`resource-card-${r.slug}`}
                className="group border border-white/10 hover:border-white/25 rounded-md p-6 transition-colors"
              >
                {r.category && (
                  <div className="text-[10px] uppercase tracking-[0.25em] text-red-500 font-bold mb-3">
                    {r.category}
                  </div>
                )}
                <div className="font-display text-lg mb-2 group-hover:text-white transition-colors">
                  {r.title}
                </div>
                {r.excerpt && (
                  <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3">{r.excerpt}</p>
                )}
                <span className="inline-flex items-center gap-1 text-sm text-red-500 mt-4">
                  Read more <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
